/* iter55z-i — CanvaPulseCard: social-proof "N authors linked Canva" widget.
 *
 * Renders in three surfaces:
 *   1. <CanvaCoverDialog> not-connected empty state
 *   2. <ConnectionsPanel> Canva card
 *   3. Customer dashboard / landing (via <CanvaPulseCard variant="wide">)
 *
 * Reads GET /api/community/canva-pulse and renders one of four modes:
 *   - "off"        → renders nothing
 *   - "aggregate"  → single stat line
 *   - "anonymized" → rotating ticker of "An author just linked Canva · Xm ago"
 *   - "named"      → ticker with first-name + last-initial (only shown for
 *                    users who explicitly opted in via /pulse/opt-in)
 *
 * Variants (visual density, not data density):
 *   - `variant="full"`    → default card with header + stat + ticker
 *   - `variant="compact"` → single line, no rotating ticker (for tight UIs)
 *   - `variant="wide"`    → full-width landing-page banner
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Users, Sparkles, Palette } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const REFRESH_MS = 60_000;
const ROTATE_MS  = 5_000;

function relTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, (Date.now() - t) / 1000);
    if (s < 60)    return 'just now';
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch { return ''; }
}

export function CanvaPulseCard({
  variant = 'full',
  className = '',
  onConnectClick,       // optional — surfaces a "Connect Canva" CTA when passed
  hidden = false,       // caller can hide (e.g., user already connected)
  testIdPrefix = 'canva-pulse',
}) {
  const [data, setData] = useState(null);
  const [idx, setIdx]   = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/community/canva-pulse`);
      setData(r.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
    const iv = window.setInterval(load, REFRESH_MS);
    return () => window.clearInterval(iv);
  }, [load]);

  // Rotate the ticker index.
  useEffect(() => {
    const list = data?.recent || [];
    if (list.length < 2) return undefined;
    const iv = window.setInterval(() => {
      setIdx((prev) => (prev + 1) % list.length);
    }, ROTATE_MS);
    return () => window.clearInterval(iv);
  }, [data]);

  if (hidden || !data || !data.enabled || data.mode === 'off') return null;

  const { mode, aggregate = {}, recent = [] } = data;
  const monthlyN = aggregate.linked_this_month || 0;
  const totalN   = aggregate.linked_total || 0;
  // Hide the whole card if there's genuinely no activity yet — this
  // prevents a stale "0 authors linked" downer message.
  if (monthlyN === 0 && totalN === 0 && recent.length === 0) return null;

  const current = recent[idx] || recent[0] || null;

  // ---- COMPACT (single-line) ----
  if (variant === 'compact') {
    return (
      <div
        data-testid={`${testIdPrefix}-compact`}
        className={`flex items-center gap-2 rounded-md border border-fuchsia-200/60 bg-fuchsia-50/50 px-2.5 py-1.5 text-[11px] text-fuchsia-900 ${className}`}
      >
        <Users className="h-3 w-3 shrink-0 text-fuchsia-600" />
        <span className="flex-1 truncate">
          <b>{monthlyN.toLocaleString()}</b> author{monthlyN === 1 ? '' : 's'} linked Canva this month
        </span>
      </div>
    );
  }

  // ---- WIDE (landing-page banner) ----
  if (variant === 'wide') {
    return (
      <section
        aria-label="Canva community activity"
        data-testid={`${testIdPrefix}-wide`}
        className={`flex flex-col gap-3 rounded-xl border border-fuchsia-200/60 bg-gradient-to-r from-fuchsia-50 to-sky-50 p-4 sm:flex-row sm:items-center ${className}`}
      >
        <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-fuchsia-100 px-3 py-1 text-[10.5px] font-semibold uppercase tracking-[0.2em] text-fuchsia-800">
          <Palette className="h-3 w-3" /> Bring Your Own Canva
        </div>
        <div className="flex-1">
          <div className="text-[13px] font-semibold text-fuchsia-950" data-testid={`${testIdPrefix}-wide-stat`}>
            {monthlyN.toLocaleString()} author{monthlyN === 1 ? '' : 's'} linked Canva this month · {totalN.toLocaleString()} total
          </div>
          {(mode === 'anonymized' || mode === 'named') && current && (
            <div key={current.id} className="mt-0.5 animate-fadeIn text-[11.5px] text-fuchsia-900/80" data-testid={`${testIdPrefix}-wide-ticker`}>
              <b>{current.display}</b> linked Canva · {relTime(current.linked_at)}
            </div>
          )}
        </div>
        {onConnectClick && (
          <button
            type="button"
            onClick={onConnectClick}
            className="shrink-0 rounded-full bg-fuchsia-600 px-4 py-1.5 text-[11.5px] font-semibold text-white shadow-sm hover:bg-fuchsia-700"
            data-testid={`${testIdPrefix}-wide-cta`}
          >
            Link my Canva <Sparkles className="ml-1 inline h-3 w-3" />
          </button>
        )}
      </section>
    );
  }

  // ---- FULL (default card) ----
  return (
    <div
      data-testid={`${testIdPrefix}-full`}
      className={`rounded-md border border-fuchsia-200/60 bg-fuchsia-50/40 p-3 ${className}`}
    >
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em] text-fuchsia-800">
        <Users className="h-3 w-3" /> Community pulse
      </div>
      <div className="mt-1 text-[13px] font-semibold text-foreground" data-testid={`${testIdPrefix}-stat`}>
        {monthlyN > 0
          ? `${monthlyN.toLocaleString()} author${monthlyN === 1 ? '' : 's'} linked Canva this month`
          : `${totalN.toLocaleString()} author${totalN === 1 ? '' : 's'} have linked Canva`}
      </div>
      {(mode === 'anonymized' || mode === 'named') && current && (
        <div key={current.id} className="mt-1 animate-fadeIn text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-ticker`}>
          <b className="text-foreground">{current.display}</b> just linked Canva · {relTime(current.linked_at)}
        </div>
      )}
      {onConnectClick && (
        <button
          type="button"
          onClick={onConnectClick}
          className="mt-2 inline-flex items-center gap-1 rounded-sm bg-fuchsia-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-fuchsia-700"
          data-testid={`${testIdPrefix}-cta`}
        >
          <Sparkles className="h-3 w-3" /> Link my Canva
        </button>
      )}
    </div>
  );
}

export default CanvaPulseCard;
